import { Component, Input, OnDestroy, OnInit, Output, EventEmitter, ViewChild, AfterViewInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DocumentSelectionInfo, MassRecipientType } from 'src/app/modules/communications/store/communications/communications.model';
import { DataTableServerComponent } from 'src/app/modules/core/components/data-table-server/data-table.component';
import { GetRowIdParams, IServerSideDatasource, RowSelectedEvent, ValueFormatterParams } from 'ag-grid-enterprise';
import { GridHelperService } from 'src/app/modules/core/services/grid-helper.service';
import { Dataset } from 'src/app/modules/core/store/models/dataset.model';
import { DocumentQueryParams, FilterGroup, FilterGroupType, RuleOperator } from 'src/app/modules/core/store/models';
import { UserContextService } from 'src/app/modules/core/services/user-context.service';
import { ClosestAssetFilterDto } from 'src/app/modules/cop/store/impacted-assets';
import { TableColumnsService } from 'src/app/modules/cop/services/table/table-columns-service';
import { ImpactedAssetsApiService } from 'src/app/modules/cop/services/impacted-assets-api.service';
import { Layer } from 'src/app/modules/cop/store/cop/models/layer.model';
import { DataApiService } from 'src/app/modules/management/store/data/data-api.service';
import { DatasetApiService } from 'src/app/modules/core/services/dataset-api.service';
import { Subject } from 'rxjs';
import { cloneDeep } from 'lodash-es';
import { CommunicationsApiService } from '@app/modules/communications/store/communications/communications-api.service';

@Component({
  selector: 'topo-comms-recipient-assets-selection-list',
  templateUrl: './recipient-assets-selection-list.component.html',
  styleUrls: ['./recipient-assets-selection-list.component.scss'],
  standalone: false
})
export class RecipientAssetsSelectionListComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() set activeContext(value: RecipientAssetsSelectionRequests) {
    this.activatedContext = value;

    if (!!this.activatedContext) {
      setTimeout(() => {
        this.dataTable?.groupRows(false);
        this.dataTable?.resetFilter();
      }, 200);

      this.loadData();
    }
  }

  activatedContext: RecipientAssetsSelectionRequests;
  currentActivatedContext: RecipientAssetsSelectionRequest;

  @Input() getSelectedUIds: (uId: string) => DocumentSelectionInfo = null;

  @Output() recipientsSelected = new EventEmitter<SelectedAssetInfo>();
  @Output() recipientsSelectionClosed = new EventEmitter();

  @ViewChild('table') dataTable: DataTableServerComponent;

  dialogLabel: string;
  headerLabel: string;
  headerIcon: string;
  datasource: any;
  columDefs: any[] = [];
  skip = 0;
  take = 10;
  totalCount: number = 0;
  searchFilterGroup: FilterGroup;
  dataset: Dataset;
  isDirty: boolean;
  selectAll: boolean;
  selectedUIds: string[];
  tableTabs: any[];
  isLoading = false;

  destroy$: Subject<void> = new Subject<void>();

  tableWidth = '70vw';
  tableHeight = '60vh';

  constructor(
    private gridHelper: GridHelperService,
    private translateService: TranslateService,
    private tableColumnsService: TableColumnsService,
    private dataApiService: DataApiService,
    private datasetApiService: DatasetApiService,
    private userContextService: UserContextService,
    private impactedAssetsApiService: ImpactedAssetsApiService,
    private commApiService: CommunicationsApiService
  ) {}

  ngOnInit(): void {
    this.datasource = this.getServerSideDatasource();

    this.dialogLabel = this.translateService.instant('COMMUNICATIONS.RECIPIENT-DETAILS');
  }

  ngAfterViewInit() {
    this.dataTable?.groupRows(false);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getServerSideDatasource(): IServerSideDatasource {
    return {
      getRows: params => {
        if (!!!this.dataset) {
          params.success({
            rowData: [],
            rowCount: 0
          });

          return;
        }
        const translatedParams = this.gridHelper.gridParamsToRequestParams(params.request);

        let columnFilter = this.tableColumnsService.getColumnFilterFromGrid(params?.request.filterModel);

        var documentQuery = {
          pagination: {
            skip: translatedParams.skip,
            take: translatedParams.take
          },
          sort: translatedParams.sort,
          filterGroup: columnFilter
        } as DocumentQueryParams;

        if (!!this.currentActivatedContext.targetDatasetUId) {
          const combinedFilterGroup: FilterGroup = {
            filterGroups: [],
            filters: [],
            groupType: FilterGroupType.And
          };

          if (!!this.searchFilterGroup) {
            combinedFilterGroup.filterGroups.push(this.searchFilterGroup);
          }
          if (!!this.currentActivatedContext.targetFilterGroup) {
            combinedFilterGroup.filterGroups.push(this.currentActivatedContext.targetFilterGroup);
          }
          if (!!documentQuery.filterGroup) {
            combinedFilterGroup.filterGroups.push(documentQuery.filterGroup);
          }
          documentQuery.filterGroup = combinedFilterGroup;

          this.dataApiService.getDocuments(this.dataset.uId, documentQuery).subscribe(
            resp => {
              const response = this.gridHelper.queryResultToGridData(resp);
              this.totalCount = response?.rowCount;

              params.success({
                rowData: response?.rowData,
                rowCount: response?.rowCount
              });
            },
            err => params.fail()
          );
        } else if (!!this.currentActivatedContext.assetsSelectionFilter) {
          var filterGroups = [];

          if (!!documentQuery.filterGroup) {
            filterGroups.push(documentQuery.filterGroup);
          }
          const layerData = this.currentActivatedContext.assetsSelectionFilter?.layerStates?.find(
            l => l.layerUId === this.currentActivatedContext.layer.uId
          );

          if (!!layerData && !!layerData?.queryMatchRuleDto?.filterGroup) {
            filterGroups.push(layerData?.queryMatchRuleDto?.filterGroup);
          }

          const combinedFilterGroup: FilterGroup = {
            filterGroups: filterGroups,
            filters: [],
            groupType: FilterGroupType.And
          };

          if (!!this.searchFilterGroup) {
            combinedFilterGroup.filterGroups.push(this.searchFilterGroup);
          }

          const assetQuery: ClosestAssetFilterDto = {
            ...this.currentActivatedContext.assetsSelectionFilter,
            layerStates: [
              {
                layerUId: this.currentActivatedContext.layer.uId,
                datasetUId: this.dataset.uId,
                queryMatchRuleDto: {
                  filterGroup: combinedFilterGroup,
                  fields: documentQuery.fields,
                  pagination: documentQuery.pagination,
                  sort: documentQuery.sort,
                  calcFields: documentQuery.calcFields
                }
              }
            ]
          };

          return this.impactedAssetsApiService.calculateClosestAssetsForLayer(assetQuery, this.currentActivatedContext.layer.uId).subscribe(
            resp => {
              const response = this.gridHelper.queryResultToGridData(resp);
              this.totalCount = response?.rowCount;

              params.success({
                rowData: response?.rowData,
                rowCount: response?.rowCount
              });
            },
            err => params.fail()
          );
        }
      }
    };
  }

  onPaginate(skip: number): void {
    this.skip = skip;
  }

  onTakeChanged(take: number): void {
    this.take = take;
  }

  get allSelected(): boolean {
    return this.selectAll === true && this.selectedUIds.length === 0;
  }

  get hasNotSelected(): boolean {
    return this.selectedUIds?.length > 0;
  }

  onSelectUnselectAll(): void {
    if (this.hasNotSelected) {
      this.onUnSelectAll();
    } else {
      if (this.allSelected === true) {
        this.onUnSelectAll();
      } else {
        this.onSelectAll();
      }
    }
  }

  onSelectData(data: any): void {}

  onRowSelected(event: RowSelectedEvent): void {
    if (this.isLoading || event.source !== 'checkboxSelected') {
      return;
    }

    if (!!!this.selectedUIds) {
      this.selectedUIds = [];
    }

    const isSelected = event.node?.isSelected();

    var uid = event.node?.data?.uid;
    if (isSelected === true) {
      if (this.selectAll !== true) {
        if (!this.selectedUIds.includes(uid)) {
          this.selectedUIds.push(uid);
          this.isDirty = true;
        }
      } else {
        const index = this.selectedUIds.indexOf(uid);
        if (index >= 0) {
          this.selectedUIds.splice(index, 1);
          this.isDirty = true;
        }
      }
    } else if (isSelected === false) {
      if (this.selectAll === true) {
        if (!this.selectedUIds.includes(uid)) {
          this.selectedUIds.push(uid);
          this.isDirty = true;
        }
      } else {
        const index = this.selectedUIds.indexOf(uid);
        if (index >= 0) {
          this.selectedUIds.splice(index, 1);
          this.isDirty = true;
        }
      }
    }

    this.submit(false);
  }

  getSelectedCount(): number {
    const selected = this.selectedUIds?.length ?? 0;
    return this.selectAll == true ? this.totalCount - selected : selected;
  }

  private generateColumns(): void {
    const addDistanceCol = !!this.currentActivatedContext.assetsSelectionFilter;
    if (!!this.dataset) {
      this.columDefs = this.tableColumnsService.getDatasetColumns(
        this.dataset,
        this.userContextService.getUserContext().organizationUId,
        addDistanceCol,
        true
      );
    }

    if (this.activatedContext.canSelect === true) {
      const column = {
        colId: '_selected',
        headerClass: 'ag-header-no-label',
        sortable: true,
        checkboxSelection: true,
        resizable: false,
        pinned: 'left',
        menuTabs: [],
        width: 40,
        maxWidth: 60,
        minWidth: 60
      };

      this.columDefs.unshift(column);
    }
  }

  formatTextForTable(params: ValueFormatterParams) {
    const text = params.value;
    if (text) {
      return text.replace(/(\r\n|\n|\r)/gm, '');
    }
    return text;
  }

  private loadData(): void {
    this.isDirty = false;
    const tabs = [];
    this.activatedContext.requests.forEach(request => {
      if (!!request.layer) {
        tabs.push({
          label: request.layer.name,
          uId: request.uId,
          iconSettings: {
            iconPath: request.layer.iconPath,
            color: request.layer.color,
            isCustomIcon: request.layer.isCustomIcon
          }
        });
      } else {
        tabs.push({
          label: '',
          uId: request.uId
        });
      }
    });
    this.tableTabs = tabs.length > 1 ? tabs : [];

    if (this.tableTabs.length > 0) {
      this.tableHeight = 'calc(80vh - 398px)';
    } else {
      this.tableHeight = 'calc(80vh - 358px)';
    }

    this.onTabClick(this.activatedContext.requests[0].uId);
  }

  onTabClick(uId: string) {
    this.currentActivatedContext = this.activatedContext.requests.find(r => r.uId == uId);

    const datasetUId = !!this.currentActivatedContext.targetDatasetUId
      ? this.currentActivatedContext.targetDatasetUId
      : this.currentActivatedContext.layer.datasetUId;
    this.datasetApiService.getDataset(datasetUId, { includeWorkflow: true }).subscribe(dataset => {
      if (!!dataset) {
        this.dataset = dataset;
        this.generateColumns();
        if (this.activatedContext.canSelect === true) {
          const selectInfo = this.getSelectedUIds(this.currentActivatedContext.uId);
          this.selectAll = selectInfo.selectAll;
          this.selectedUIds = cloneDeep(selectInfo.documentUIds);
        }
        this.skip = 0;
        this.take = 10;
        if (!!this.currentActivatedContext.layer) {
          this.dialogLabel = this.translateService.instant('COMMUNICATIONS.DETAIL-CLOSEST-ASSETS');
          this.headerLabel = this.currentActivatedContext.layer?.name;
        } else if (!!this.currentActivatedContext.targetDatasetUId) {
          this.dialogLabel = this.currentActivatedContext.parentDatasetName;
          this.headerLabel = this.translateService.instant('COMMUNICATIONS.DETAIL-RELATED-ASSETS', { name: this.dataset.name });
        }
      }
    });
  }

  close(): void {
    this.recipientsSelectionClosed.emit();
  }

  cancelClick(): void {
    this.close();
  }

  private submit(close: boolean) {
    const type = MassRecipientType.ClosestAssets;
    const selections = {
      uId: this.currentActivatedContext.uId,
      selectInfo: {
        selectAll: this.selectAll,
        documentUIds: []
      }
    } as SelectedAssetInfo;

    this.selectedUIds?.forEach(s => {
      selections.selectInfo.documentUIds.push(s);
    });

    this.recipientsSelected.emit(selections);

    this.isDirty = false;

    if (close) {
      this.close();
    }
  }

  onSearchFilterChanged(filterGroup: FilterGroup): void {
    this.searchFilterGroup = filterGroup;
  }

  onGridDataLoaded(): void {
    this.dataTable?.gridApi?.sizeColumnsToFit();
    this.isLoading = false;
  }

  onRowsChanged(): void {
    if (this.activatedContext.canSelect === true) {
      this.dataTable?.gridApi?.forEachNode(node => {
        if (!node.isSelected()) {
          const shouldSelect = this.selectAll || this.selectedUIds?.includes(node.id);

          if (shouldSelect) {
            node.setSelected(true, false, null);
          }
        }
      });
    }
  }

  private onSelectAll(): void {
    this.selectAll = true;
    this.selectedUIds = [];
    this.dataTable?.gridApi?.selectAll();
    this.isDirty = true;
    this.submit(false);
  }

  private onUnSelectAll(): void {
    this.selectAll = false;
    this.selectedUIds = [];
    this.dataTable?.gridApi?.deselectAll();
    this.isDirty = true;
    this.submit(false);
  }

  getRowId = (params: GetRowIdParams): string => {
    return params.data.uid;
  };
}

export interface RecipientAssetsSelectionRequests {
  requests: RecipientAssetsSelectionRequest[];
  canSelect?: boolean;
  canSelectAll?: boolean;
}

export interface RecipientAssetsSelectionRequest {
  uId: string;
  layer?: Layer;
  assetsSelectionFilter?: ClosestAssetFilterDto;
  assetDatasetUId: string;
  parentDatasetName?: string;
  targetDatasetUId?: string;
  targetFilterGroup?: FilterGroup;
}

export interface SelectedAssetInfo {
  uId?: string;
  selectInfo: DocumentSelectionInfo;
}
